import { Button, Card, Flex, Text } from '@chakra-ui/react';
import { ConfirmModal, createDataCollectionContext, dbTimeToUserTime, FormInput, parseErrorToString, toaster, type ConfirmModalHandle } from '@shared';
import { useTranslation } from 'react-i18next';
import inboundMessagesService from '../../api/services/inboundMessagesService';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo, useRef, type RefObject } from 'react';
import { DateTime } from 'luxon';
import { useAuth } from '../../context/AuthContext';
import { useValidatedSorting } from '@shared';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

const columnHelper = createColumnHelper<InboundMessage>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

function CommentModal({ inboundMessageID, comment, ref }: { inboundMessageID: number; comment: string; ref: RefObject<ConfirmModalHandle | null> }) {
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	const methods = useForm<{ comment: string }>({
		defaultValues: {
			comment: comment,
		},
	});

	async function handleSubmit(data: { comment: string }) {
		await inboundMessagesService.update({ inboundMessageID: inboundMessageID, comment: data.comment });
		queryClient.invalidateQueries({ queryKey: ['inboundMessages'] });
		toaster.create({
			type: 'success',
			title: t('general.saved'),
		});
		ref.current?.close();
	}

	return (
		<FormProvider {...methods}>
			<Flex flexDir="column" as="form" onSubmit={methods.handleSubmit(handleSubmit)}>
				<FormInput label={t('general.comment')} name="comment" maxLength={500} />

				<Flex gap={4} mt={4}>
					<Button variant={'outline'} onClick={ref.current?.close}>
						{t('general.cancel')}
					</Button>
					<Button id="confirmButton" type="submit">
						{t('general.save')}
					</Button>
				</Flex>
			</Flex>
		</FormProvider>
	);
}

export default function InboundMessages() {
	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(
		() => createDataCollectionContext<InboundMessage>(),
		[],
	);

	const { t } = useTranslation();
	const addComment = useRef<ConfirmModalHandle>(null);

	function openCommentModal(inboundMessageID: number, comment: string): void {
		addComment.current?.open({
			title: t('general.add_comment'),
			content: <CommentModal inboundMessageID={inboundMessageID} comment={comment} ref={addComment} />,
			isBackButtonVisible: false,
			isConfirmButtonVisible: false,
		});
	}

	function openMessageModal(message: string): void {
		addComment.current?.open({
			title: t('general.message'),
			content: <Text>{message}</Text>,
			isConfirmButtonVisible: false,
			backButtonText: t('general.close_modal'),
		});
	}

	const columns = [
		columnHelper.accessor('contact.firstName', {
			id: 'contacts.firstName',
			header: t('general.first_name'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('contact.lastName', {
			id: 'contacts.lastName',
			header: t('general.last_name'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('contact.postalAddress', {
			header: t('general.postal_address'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('contact.postalCode', {
			header: t('general.postal_code'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('contact.city', {
			header: t('general.city'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('senderPhone', {
			header: t('general.phone'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('contact.email', {
			id: 'contacts.email',
			header: t('general.email'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('message', {
			header: t('general.message'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.original.message}</Text>;
			},
		}),
		columnHelper.accessor('comment', {
			header: t('general.comment'),
			enableSorting: false,
			cell: ({ row }) => {
				return (
					<Text
						cursor={'pointer'}
						textDecor={'underline'}
						style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}
						onClick={() => {
							openCommentModal(row.original.inboundMessageID, row.original.comment);
						}}
					>
						{row.original.comment || `+ ${t('general.add_comment')}`}
					</Text>
				);
			},
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			filterFn: 'dateTimeBetween',
			meta: {
				filterVariant: 'dateTime',
				filterLabel: [t('general.time_received_after'), t('general.time_received_before')],
			},
			cell: ({ row }) => {
				return <Text>{dbTimeToUserTime(row.original.timeCreated, TIMEZONE)}</Text>;
			},
		}),
	];

	useValidatedSorting('inboundMessages', columns);

	return (
		<Card.Root data-cy="messageHistoryCard" maxWidth={'2000px'} width="100%" flex={1} minWidth={'340px'}>
			<Card.Body flexWrap={'wrap'} display={'flex'} flexDirection={'row'} justifyContent={'space-between'} padding={5} gap={5}>
				<Flex display={'flex'} flexDirection={'column'} gap={3} flex={1} maxWidth={'100%'}>
					<DataCollectionContextProvider
						dataKey="inboundMessages"
						keyAttribute="inboundMessageID"
						emptyDataText={t('general.no_messages_found')}
						fetchFn={(...args) => inboundMessagesService.getAllResources(...args)}
						queryParams={{ getRelated: 'contact' }}
						initialItemsPerPage={25}
						columns={columns}
						defaultHiddenColumns={[]}
						isServerSide={true}
						isSelectable={false}
						defaultSort={{ id: 'timeCreated', desc: true }}
						onClickRow={row => {
							openMessageModal(row.original.message);
						}}
					>
						<Flex gap={2} flexWrap={'wrap'}>
							<DataCollectionTableHeader />
							<ConfirmModal ref={addComment} />

							<Export />
						</Flex>
						<DataCollectionTable />
						<Flex gap={2}>
							<DataCollectionTableFooter />
						</Flex>
					</DataCollectionContextProvider>
				</Flex>
			</Card.Body>
		</Card.Root>
	);
}

function Export() {
	const { t } = useTranslation();
	const { currentAccount } = useAuth();

	function getFilters(): string {
		const url = window.location.href;

		const queryIndex = url.indexOf('?');
		if (queryIndex === -1) return '';

		const queryString = url.substring(queryIndex + 1);
		const params = new URLSearchParams(queryString);

		// Construct the filters part (everything after "tab=")
		const tab = params.get('tab');
		if (!tab) return '';

		const filters: string[] = [];

		let foundTab = false;
		for (const [key, value] of params.entries()) {
			if (key === 'tab') {
				foundTab = true;
				continue; // skip 'tab' itself
			}
			if (foundTab) {
				filters.push(`${key}=${value}`);
			}
		}

		return filters.join('&') || '';
	}

	async function handleExport(): Promise<void> {
		try {
			const response = await inboundMessagesService.export(getFilters());

			const now = DateTime.now();
			const formattedNow = now.toFormat('yyyy-MM-dd');
			const filename = `${currentAccount?.name}-inbound-messages-export-${formattedNow}.xlsx`.toLowerCase().replace(/\s+/g, '-');

			const blob = new Blob([response.data], {
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			});

			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', filename);
			document.body.appendChild(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(url);
		} catch (error) {
			toaster.create({
				type: 'error',
				title: parseErrorToString(error),
			});
		}
	}

	return <Button onClick={handleExport}>{t('general.export')}</Button>;
}
